import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ChevronRight, Plane, RefreshCw } from 'lucide-react';
import { User } from '@/api/entities';
import { getApiUrl } from '@/lib/apiUrls';
import { cn } from '@/lib/utils';
import { getCityName } from '@/components/airportUtils';
import { TRIPS_LIST_ITEM_SHELL } from '@/constants/brandColors';
import LiveFlightsMap from '@/components/admin/LiveFlightsMap';
import { VipPill } from '@/components/admin/VipBadge';
import { STATUS_TONE_CLASSES, aircraftName, clockAt, countdownTo, updatedAgo } from '@/utils/flightStatusDisplay';
import { altitudeLine, flightProgressOf, liveFlightBadge, tripName } from '@/utils/liveFlights';

/**
 * Live Flights (admins): every Defera traveler in the air right now, the way
 * Flighty shows your flights. The map on top, on the trip page's flight map
 * colours (src/components/admin/LiveFlightsMap.jsx); under it, a card for
 * each flight in the air, then the ones leaving in the next few hours and
 * the ones that just landed. Tapping a flight in the air picks it out on the
 * map. Data: GET /api/admin/live-flights (server/live-flights.js), every
 * minute.
 */

const REFRESH_MS = 60 * 1000;

export default function LiveFlights() {
  const [currentUser, setCurrentUser] = useState(null);
  const [checkedUser, setCheckedUser] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const mapRef = useRef(null);

  const canAccess = Boolean(currentUser) && (currentUser.role === 'admin' || currentUser.app_metadata?.roles?.includes('admin'));

  useEffect(() => {
    (async () => {
      try {
        setCurrentUser(await User.me());
      } catch {
        setCurrentUser(null);
      } finally {
        setCheckedUser(true);
      }
    })();
  }, []);

  const load = useCallback(async () => {
    try {
      setRefreshing(true);
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not signed in');
      const resp = await fetch(getApiUrl('admin/live-flights'), {
        headers: { Authorization: `Bearer ${session.access_token}` },
        credentials: 'omit',
      });
      const json = await resp.json();
      if (!resp.ok || !json.ok) throw new Error(json?.error || `Request failed (${resp.status})`);
      setData(json);
      setError(null);
    } catch (e) {
      setError(e?.message || 'Live flights are unavailable right now.');
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (!canAccess) return undefined;
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [canAccess, load]);

  // Countdowns and the planes between reads move with the clock.
  useEffect(() => {
    const timer = setInterval(() => setNowMs(Date.now()), 30 * 1000);
    return () => clearInterval(timer);
  }, []);

  const inAir = useMemo(() => data?.in_air || [], [data]);
  const departing = useMemo(() => data?.departing || [], [data]);
  const landed = useMemo(() => data?.landed || [], [data]);

  if (!checkedUser) {
    return <div className="min-h-[70vh] flex items-center justify-center px-4"><p className="text-white/60 font-normal">Loading…</p></div>;
  }
  if (!canAccess) {
    return <div className="min-h-[70vh] flex items-center justify-center px-4"><p className="text-white/85 font-normal">Administrator access required.</p></div>;
  }

  const select = (id) => setSelectedId((cur) => (cur === id ? null : id));
  // A card tapped below the map brings the map back into view, as Flighty does.
  const selectFromList = (id) => {
    select(id);
    const top = mapRef.current?.getBoundingClientRect().top;
    if (top != null && top < 0) mapRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <Helmet>
        <title>Live Flights - Defera</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8 py-5 sm:py-7 overflow-x-hidden">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-sky-400/[0.1] border border-sky-400/20 flex items-center justify-center shrink-0">
              <Plane className="w-4 h-4 text-sky-200" strokeWidth={1.5} />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-extralight text-white leading-tight">Live Flights</h1>
              <p className="text-white/45 text-[13px] font-normal leading-snug">Every Defera traveler in the air, right now.</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {data?.now && <span className="text-[11px] sm:text-xs text-white/40 font-normal">{updatedAgo(data.now, nowMs)}</span>}
            <button
              type="button"
              onClick={load}
              disabled={refreshing}
              className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-white/[0.04] border border-white/[0.08] text-[13px] font-medium text-white/70 hover:text-white transition disabled:opacity-50"
            >
              <RefreshCw className={cn('w-3.5 h-3.5 shrink-0', refreshing && 'animate-spin')} />
              Refresh
            </button>
          </div>
        </div>

        <div ref={mapRef} className="scroll-mt-4">
          <LiveFlightsMap
            inAir={inAir}
            departing={departing}
            selectedId={selectedId}
            onSelect={select}
            nowMs={nowMs}
            className="h-[46vh] min-h-[300px] sm:h-[56vh] rounded-2xl sm:rounded-3xl border border-sky-400/10 shadow-[0_0_40px_-18px_rgba(56,189,248,0.55)]"
          />
        </div>

        {error && (
          <p className="mt-4 rounded-2xl border border-rose-400/25 bg-rose-500/[0.06] px-4 py-3 text-[13px] text-rose-100">{error}</p>
        )}
        {data && data.enabled === false && (
          <p className="mt-4 text-[13px] text-white/60">Live flight status is off, so there are no flights to show.</p>
        )}

        <div className="mt-5 space-y-6">
          <Section title="In the air" count={inAir.length} empty={data ? 'No one is flying right now.' : null}>
            {inAir.map((f) => (
              <FlightCard key={f.id} flight={f} kind="in_air" nowMs={nowMs} selected={f.id === selectedId} onSelect={() => selectFromList(f.id)} />
            ))}
          </Section>
          <Section title="Leaving in the next 6 hours" count={departing.length} empty={data ? 'No departures in the next 6 hours.' : null}>
            {departing.map((f) => <FlightCard key={f.id} flight={f} kind="departing" nowMs={nowMs} />)}
          </Section>
          {landed.length > 0 && (
            <Section title="Landed in the last 3 hours" count={landed.length}>
              {landed.map((f) => <FlightCard key={f.id} flight={f} kind="landed" nowMs={nowMs} />)}
            </Section>
          )}
        </div>
      </div>
    </>
  );
}

function Section({ title, count, empty = null, children }) {
  return (
    <section>
      <h2 className="mb-2.5 flex items-center gap-2 text-[11px] sm:text-xs uppercase tracking-[0.18em] text-white/50 font-normal">
        {title}
        <span className="rounded-full bg-white/[0.06] px-2 py-0.5 text-white/70 tracking-normal tabular-nums">{count}</span>
      </h2>
      {count > 0 ? <div className="grid gap-3 md:grid-cols-2">{children}</div> : (empty && <p className="text-[13px] text-white/45">{empty}</p>)}
    </section>
  );
}

Section.propTypes = {
  title: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  empty: PropTypes.string,
  children: PropTypes.node,
};

/** One flight, the way Flighty lays it out: flight and status, the two airports with the plane between, then the times. */
function FlightCard({ flight, kind, nowMs, selected = false, onSelect = null }) {
  const badge = liveFlightBadge(flight, kind);
  const tone = STATUS_TONE_CLASSES[badge.tone] || STATUS_TONE_CLASSES.muted;
  const progress = kind === 'in_air' ? flightProgressOf(flight, nowMs) : kind === 'landed' ? 1 : 0;
  const plane = aircraftName(flight.aircraft_type);
  const height = kind === 'in_air' ? altitudeLine(flight.position) : null;

  let left;
  let right;
  if (kind === 'in_air') {
    left = { verb: 'Departed', time: clockAt(flight.departed_at, flight.origin_timezone) };
    const lands = countdownTo(flight.eta, nowMs);
    right = { verb: lands ? `Lands in ${lands}` : 'Arrives', time: clockAt(flight.eta, flight.destination_timezone) };
  } else if (kind === 'departing') {
    const leaves = countdownTo(flight.out_at, nowMs);
    left = { verb: leaves ? `Leaves in ${leaves}` : 'Departs', time: clockAt(flight.out_at, flight.origin_timezone), detail: [flight.terminal && `Terminal ${flight.terminal}`, flight.gate && `Gate ${flight.gate}`].filter(Boolean).join(' · ') || null };
    right = null;
  } else {
    left = null;
    right = { verb: flight.at_gate ? 'At the gate' : 'Landed', time: clockAt(flight.landed_at, flight.destination_timezone), detail: flight.baggage_claim ? `Bags: carousel ${flight.baggage_claim}` : null };
  }

  const main = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[15px] text-white font-medium truncate">{flight.flight || 'Flight'}</p>
          <p className="text-[12px] text-white/50 font-normal truncate">{[flight.airline, plane].filter(Boolean).join(' · ')}</p>
        </div>
        <span className={cn('inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[11px] font-normal', tone.pill)}>
          <span className={cn('h-1.5 w-1.5 rounded-full', tone.dot)} aria-hidden="true" />
          {badge.label}
        </span>
      </div>

      <div className="mt-3 flex items-center gap-3">
        <Airport code={flight.origin} />
        <div className="relative h-4 flex-1" aria-hidden="true">
          <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 border-t border-dashed border-white/20" />
          {progress > 0 && <div className="absolute left-0 top-1/2 h-px -translate-y-1/2 bg-sky-300/80" style={{ width: `${progress * 100}%` }} />}
          <Plane className="absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rotate-45 text-white" style={{ left: `${(kind === 'departing' ? 0.04 : Math.min(0.96, Math.max(0.04, progress))) * 100}%` }} />
        </div>
        <Airport code={flight.destination} alignRight />
      </div>

      <div className="mt-2 flex items-start justify-between gap-3 text-[12px]">
        <Time end={left} />
        <Time end={right} alignRight />
      </div>
    </>
  );

  return (
    <div className={cn('relative overflow-hidden rounded-2xl sm:rounded-3xl', selected && 'ring-1 ring-sky-300/60')}>
      <div className={`absolute inset-0 ${TRIPS_LIST_ITEM_SHELL}`} />
      {onSelect ? (
        <button type="button" onClick={onSelect} aria-pressed={selected} aria-label={`Show ${flight.flight || 'this flight'} on the map`} className="relative block w-full p-4 pb-0 text-left">
          {main}
        </button>
      ) : (
        <div className="relative p-4 pb-0">{main}</div>
      )}
      <div className="relative mx-4 mt-3 mb-3.5 flex items-center justify-between gap-3 border-t border-white/[0.06] pt-2.5 text-[12px]">
        <span className="min-w-0 truncate text-white/60">
          <Link to={`/trips?tripId=${encodeURIComponent(flight.booking_id)}`} className="text-sky-200 hover:text-sky-100 underline-offset-2 hover:underline">
            {tripName(flight.trip)}
          </Link>
          {flight.trip?.vip ? <VipPill className="ml-1.5 align-middle" /> : null}
          {height && <span className="text-white/45"> · {height}</span>}
        </span>
        <ChevronRight className="h-4 w-4 flex-shrink-0 text-white/35" aria-hidden="true" />
      </div>
    </div>
  );
}

FlightCard.propTypes = {
  flight: PropTypes.object.isRequired,
  kind: PropTypes.oneOf(['in_air', 'departing', 'landed']).isRequired,
  nowMs: PropTypes.number.isRequired,
  selected: PropTypes.bool,
  onSelect: PropTypes.func,
};

function Airport({ code, alignRight = false }) {
  return (
    <div className={cn('min-w-0 flex-shrink-0', alignRight && 'text-right')}>
      <p className="text-2xl font-medium text-white tracking-tight leading-none">{code || '—'}</p>
      <p className="mt-1 max-w-[7.5rem] truncate text-[11px] text-white/50 font-normal">{code ? getCityName(code) : ''}</p>
    </div>
  );
}

Airport.propTypes = { code: PropTypes.string, alignRight: PropTypes.bool };

function Time({ end, alignRight = false }) {
  if (!end) return <span />;
  return (
    <div className={cn('min-w-0', alignRight && 'text-right')}>
      <p className="text-white/50 font-normal">{end.verb}</p>
      {end.time && <p className="text-[14px] text-white font-normal tabular-nums">{end.time}</p>}
      {end.detail && <p className="text-white/60 font-normal">{end.detail}</p>}
    </div>
  );
}

Time.propTypes = {
  end: PropTypes.shape({ verb: PropTypes.string, time: PropTypes.string, detail: PropTypes.string }),
  alignRight: PropTypes.bool,
};
